import { Box, Divider, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mapByRefValue } from "../../../components/Details/Details";
import { mapRefValuesToHorizontalDetails } from "../../../components/Details/HorizonticalWrapper";
import DetailsOASkeleton from "../../../components/Skeletons/DetailsOASkeleton";
import {
  fetchContributors,
  fetchErgoDetails,
  fetchSullogesDetails,
} from "../../../lib/fetchers";
import {
  _projectBasicInfo,
  _projectEducationalInfo,
} from "../../../lib/static/oa/ergo/details";
import Theme from "../../../Themes/main.theme";
import DetailsErgo from "./DetailsErgo";
import {
  basicProjectDetails,
  educationalProjectDetails,
  mapProjectDetails,
} from "./helperFuncs";
import { useContext } from "react";
import BreadcrumbContext from "../../../components/context/BreadcrumbContext";
import { useTranslation } from "react-i18next";
import EmptyPage from "../../../components/EmptyPage/EmptyPage";
import i18n from "../../../i18n";
import { transformGreekToEnglish } from "../../../lib/helperFuncs";

function SmartDetailsErgo() {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [projectInfo, setProjecInfo] = useState<any>([]);
  const [syntelestes, setSyntelestes] = useState<any>([]);
  const [educationalInfo, setEducationalInfo] = useState<any>([]);
  const [locations, setLocations] = useState<any>([]);
  const [projects, setProjects] = useState<any>([]);
  const [title, setTitle] = useState<string>("");
  const [titleEN, setTitleEN] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState<string | any>();
  const { setName } = useContext(BreadcrumbContext.BreadcrumbProvider.context);

  const fetchData = async () => {
    try {
      setName("");
      const detailsResp = await fetchErgoDetails(id);

      if (!detailsResp?.item?.["o:is_public"]) throw new Error();

      const relatedContributor =
        detailsResp?.item["projects:projectConnectorWithContributor"];

      const contributorsRoles = !relatedContributor
        ? []
        : detailsResp?.item["projects:projectConnectorWithContributor"].map(
          (x: any) => {
            return {
              id: x["connectors:connectorRelationWithContributor"]?.[0]?.[
                "value_resource_id"
              ],
              role: t(x["connectors:connectorRole"][0]["@value"]),
            };
          }
        );

      const contributorsIds = !relatedContributor
        ? []
        : detailsResp?.item?.["projects:projectConnectorWithContributor"].map(
          (x: any) => {
            return {
              id: x["connectors:connectorRelationWithContributor"]?.map(
                (item: any) => item?.["value_resource_id"]
              )[0],
            };
          }
        );

      const arrayOfContributorsIds = relatedContributor
        ? contributorsIds.map((y: any) => y.id)
        : [];
      const detailsOfContributors = await fetchContributors(
        arrayOfContributorsIds
      );
      const x = relatedContributor
        ? detailsOfContributors?.data?.data.map((x: any) => {
          return {
            id: x?.["o:id"],
            name:
              i18n.language === "el"
                ? x?.["contributors:contributorFullName"]?.[0]?.["@value"]
                : transformGreekToEnglish(
                  t(x["contributors:contributorFullName"]?.[0]?.["@value"])
                ),
            pseudonymo:
              i18n.language === "el"
                ? x?.["contributors:contributorNickname"]?.[0]?.["@value"]
                : transformGreekToEnglish(
                  t(x["contributors:contributorNickname"]?.[0]?.["@value"])
                ),
            bornDate: x["contributors:contributorBirthYear"]?.[0]?.["@value"],
            capacity: relatedContributor
              ? contributorsRoles.find((role: any) => role.id === x["o:id"])
                ?.role || "-"
              : [],
          };
        })
        : [];

      document.title =
        i18n.language === "el"
          ? detailsResp?.item?.["o:title"]
          : detailsResp?.item?.["projects:projectEnTitle"]?.[0]?.["@value"];
      setName(
        i18n.language === "el"
          ? detailsResp?.item?.["o:title"]
          : detailsResp?.item?.["projects:projectEnTitle"]?.[0]?.["@value"]
      );
      setTitle(detailsResp?.item?.["o:title"]);
      setTitleEN(
        detailsResp?.item?.["projects:projectEnTitle"]?.[0]?.["@value"]
      );

      const _mappedMetadata = mapProjectDetails(detailsResp.item);
      const relatedCollections =
        detailsResp?.reverse?.["documents:documentsRelationProject"]
          ?.data?.[0]?.["documents:documentsRelationCollection"]?.[0]?.[
            "value_resource_id"
          ];

      const collectionID = relatedCollections
        ? await fetchSullogesDetails(relatedCollections)
        : [];
      const collection_en_title =
        collectionID.item?.["collections:collectionsEnTitle"]?.[0]?.["@value"];

      const documentDetails = (fetchData: any) => {
        return fetchData.reverse["documents:documentsRelationProject"].data.map(
          (x: any) => {
            return {
              id: x["o:id"],
              title:
                i18n.language === "el"
                  ? x["documents:documentsTitle"]?.map(
                    (item: any) => item?.["@value"]
                  )[0]
                  : x["documents:documentsEnTitle"]?.map(
                    (item: any) => item?.["@value"]
                  )[0],
              type: x["documents:documentsType"]?.map(
                (item: any) => item?.["@value"]
              )[0],
              collectionTitle:
                i18n.language === "el"
                  ? x["documents:documentsRelationCollection"]?.map(
                    (item: any) => item?.["display_title"]
                  )
                  : collection_en_title,
              url: `/search-documents/details/${x["o:id"]}`,
            };
          }
        );
      };

      const projectLocations =
        detailsResp?.item?.["projects:projectRelationWithLocation"]?.length > 0
          ? detailsResp?.item?.["projects:projectRelationWithLocation"]?.map(
            (x: any) => {
              if (x.is_public !== false) {
                return {
                  id: x.value_resource_id,
                  is_public: x.is_public,
                  name: x.display_title,
                  value: "",
                  url: `/location/details/${x.value_resource_id}`,
                };
              } else null;
            }
          )
          : [];

      const filteredProjectLocations = projectLocations.filter(
        (item: any) => item.name !== undefined || item.id !== undefined
      );
      const y = filteredProjectLocations.map((x: any) => {
        return {
          ...x,
          name:
            i18n.language === "el"
              ? x.name
              : transformGreekToEnglish(x.name),
        };
      });

      let results = documentDetails(detailsResp);
      results = results.map((x: any) => {
        return {
          ...x,
          type: t(x.type),
        };
      });

      setProjects(results);
      setSyntelestes(x);
      setLocations(y);
      const mappedOmekaToRefValue = mapByRefValue(
        _mappedMetadata,
        _projectBasicInfo(_mappedMetadata).map((x) => {
          return {
            ...x,
            name: t(x.name),
          };
        })
      );

      const horizontalRefValue = basicProjectDetails;
      const newValue = mapRefValuesToHorizontalDetails(
        horizontalRefValue,
        mappedOmekaToRefValue?.map((x: any) => {
          return {
            ...x,
            value:
              i18n.language === "el"
                ? x?.value
                : transformGreekToEnglish(t(x?.value[0])),
          };
        })
      );
      setProjecInfo(newValue);

      const mappedOmekaToRefValues = mapByRefValue(
        _mappedMetadata,
        _projectEducationalInfo(_mappedMetadata).map((x) => {
          return {
            ...x,
            name: t(x.name),
          };
        })
      );

      const horizontalRefValues = educationalProjectDetails;
      const newValues = mapRefValuesToHorizontalDetails(
        horizontalRefValues,
        mappedOmekaToRefValues?.map((x: any) => {
          return {
            ...x,
            value:
              i18n.language === "el"
                ? x?.value
                : transformGreekToEnglish(t(x?.value[0])),
          };
        })
      );
      setEducationalInfo(newValues);
      setIsLoading(false);
    } catch (error) {
      enqueueSnackbar(
        t("Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα"),
        { variant: "error" }
      );
      console.error(error);
      setErrorMsg(t("Η σελίδα που ζητήσατε δεν βρέθηκε."));
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      {isLoading
        ? (
          <DetailsOASkeleton length={4} />
        )
        : errorMsg
          ? (
            <Box id='container-details' sx={{ m: 2, height: "600px" }}>
              <EmptyPage errorMsg={errorMsg} />
            </Box>
          )
          : (
            <Box id='container-details' width='100%'>
              <Typography
                sx={{ my: 2 }}
                variant='h2'
                color={Theme?.labelColor?.main}
              >
                {title}
              </Typography>
              <Typography
                sx={{ my: 2 }}
                variant='h2'
                color={Theme?.labelColor?.main}
              >
                {titleEN}
              </Typography>
              <Divider
                sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }}
              />
              <DetailsErgo
                locations={locations}
                projectInfo={projectInfo}
                syntelestes={syntelestes}
                educationalInfo={educationalInfo}
                projects={projects}
              />
            </Box>
          )}
    </>
  );
}

export default SmartDetailsErgo;
